import { createToaster } from '@chakra-ui/react/toast';
import { type ReactNode } from 'react';

import { type NotificationVariant } from '../../theme/common/AlertAndToast.common';
import { type BitkitDataAttributes } from '../../utilities/dataAttributes';
import { type NotificationAction } from '../common/notificationMaps';

export type BitkitToastVariant = NotificationVariant;

export type BitkitToastProps = {
  action?: NotificationAction;
  dismissible?: boolean;
  /**
   * How long the toast stays visible, in milliseconds. Overrides the per-variant
   * default for any variant: 5000ms for `ai`/`info`/`success`/`warning`, `Infinity`
   * for `critical` and `progress`. Pass `Infinity` to make a toast persist.
   */
  duration?: number;
  messageText: ReactNode;
  timestamp?: string;
  titleText?: ReactNode;
  variant: BitkitToastVariant;
  // Data attributes are forwarded to the toast's root element. `createBitkitToast` is a function
  // rather than a component, so props can't be spread onto anything directly — these travel
  // through the toast store's `meta` and are applied when it renders. The case it exists for is
  // `data-clarity-unmask`: without it a migrated error toast shows up as blocks in session replay,
  // exactly where support needs to read what went wrong.
} & BitkitDataAttributes;

// Zag's internal priority table only knows its 5 built-in types and crashes
// on our custom variants (ai/critical/progress). We replicate Zag's algorithm
// with our full variant set and compute priority ourselves, so the broken
// lookup is never reached. Each tuple is [actionable, nonActionable] — mirrors
// Zag, which ranks actionable toasts higher. Lower number = higher in the stack.
const TOAST_PRIORITIES = {
  critical: [1, 2], // ~ Zag "error"   — most urgent
  warning: [3, 6], //  = Zag "warning"
  progress: [4, 5], // ~ Zag "loading"
  success: [5, 7], //  = Zag "success"
  ai: [6, 8], //       ~ Zag "info"
  info: [6, 8], //     = Zag "info"    — least urgent
} as const satisfies Record<BitkitToastVariant, readonly [number, number]>;

// Same name-collision problem as the priorities above: Zag keys its timeout table
// by its own type names, so only info/warning/success happen to match ours and the
// rest silently fall back to its 5000ms DEFAULT. We own the full table instead.
// `critical` and `progress` persist until dismissed — an error the user must see,
// and an operation that is still running, should not disappear on a timer.
const TOAST_DURATIONS = {
  ai: 5000,
  critical: Infinity,
  info: 5000,
  progress: Infinity,
  success: 5000,
  warning: 5000,
} as const satisfies Record<BitkitToastVariant, number>;

export const toaster = createToaster({
  max: 5,
  placement: 'top-end',
  pauseOnPageIdle: true,
});

const createBitkitToast = (props: BitkitToastProps) => {
  const { action, dismissible = true, duration, messageText, timestamp, titleText, variant, ...dataAttributes } = props;

  const [actionable, nonActionable] = TOAST_PRIORITIES[variant];

  return toaster.create({
    closable: dismissible,
    description: messageText,
    duration: duration ?? TOAST_DURATIONS[variant],
    meta: { action, dataAttributes, timestamp },
    priority: action ? actionable : nonActionable,
    title: titleText,
    type: variant,
  });
};

/**
 * Closes the toast with the given id — the one `createBitkitToast` returns — or every
 * visible toast when called without an id — note that an `undefined` id counts as "no id",
 * so guard ids that may not be set yet. Use it to close `duration: Infinity` toasts
 * (`critical`, `progress`) from code once the operation they report on ends, and to clean
 * up toasts between tests. The node is unmounted 200ms after the call — Zag's fixed
 * `removeDelay`, which cuts the exit transition short rather than waiting it out — so in
 * tests, assert its absence with `waitFor` or advance the timers.
 */
export const dismissBitkitToast = (id?: string) => toaster.dismiss(id);

export default createBitkitToast;
